import React, { FC, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SegmentEditor, { SegmentTemplateDefaults } from ".";
import { BasicDialog } from "../BasicDialog";
import {
	CmVisitScheduleOption,
	RewardEngineOption,
	TreatmentSegment,
} from "../../domain/treatmentPlan.interface";
import { Event } from "../../domain/event.interface";
import {
	activityHasOwnSegments,
	followTemplateHelperText,
	hasSegments,
	NO_SEGMENTS_MESSAGE,
	seedActivitySegments,
	segmentHeaderSummary,
} from "./segmentEditor.helpers";

/**
 * SIMS-9225 (FR-16, D1; Hi-Fi v2 Screen 2, badges 6–8) — an additional activity's Program Segments.
 *
 * State A, following the template: the template's current list as read-only rows and one action,
 * DEFINE OWN SEGMENTS, which seeds the activity's own list from a copy of it (AC3). State B, own
 * segments: the shared SegmentEditor on the activity's own list (AC1). Which state shows is the
 * activity's `segments` key: present (even []) is its own, absent follows the template (AC2).
 *
 * SIMS-9226 AC3 (badge 9): FOLLOW TEMPLATE SEGMENTS at the foot of state B, confirmed through the
 * existing BasicDialog, hands control back to the caller, which DELETES the activity's key (never
 * `[]`). Inside the own editor the off-switch hints name the activity (Q-6 = A).
 *
 * Test ids (Q-7): `segment-editor-activity-{index}` wraps the block in both states — activities
 * carry no stored `_id`, so the position in the template's list is the handle. Synthetic data only.
 */
interface ActivitySegmentsProps {
	/** 0-based position of the activity in its template's `additionalTreatmentConfigs`. */
	activityIndex: number;
	/** The activity's own list; `undefined` while it follows the template. */
	segments?: TreatmentSegment[];
	/** Called with the activity's new own list (DEFINE OWN SEGMENTS, then every edit). */
	onChange: (segments: TreatmentSegment[]) => void;
	/** The template's name, as typed (rendered uppercase by CSS, never stored uppercased). */
	templateName?: string;
	/** The template's list as it is in the form now, including unsaved edits. */
	templateSegments: TreatmentSegment[];
	/** The template's reward engine: what an inherited row without an override shows. */
	templateRewardEngineId?: string;
	/** The activity's own fields: what a segment of its own list inherits with an override OFF. */
	activityDefaults: SegmentTemplateDefaults;
	rewardEngineOptions: RewardEngineOption[];
	behaviors: Event[];
	cmVisitScheduleOptions: CmVisitScheduleOption[];
	/** The activity's name, as typed: the source the own editor's hints name (Q-6). */
	activityName?: string;
	/** SIMS-9226 AC3: confirmed FOLLOW TEMPLATE SEGMENTS — the caller removes the `segments` key. */
	onFollowTemplate: () => void;
	/** SIMS-9231 AC4: save-time inert-threshold notices for the activity's own segments. */
	inertThresholdNotices?: Record<number, string>;
}

const ActivitySegments: FC<ActivitySegmentsProps> = ({
	activityIndex,
	segments,
	onChange,
	templateName,
	templateSegments,
	templateRewardEngineId,
	activityDefaults,
	rewardEngineOptions,
	behaviors,
	cmVisitScheduleOptions,
	activityName,
	onFollowTemplate,
	inertThresholdNotices,
}) => {
	const name = templateName?.trim();
	const activityLabel = activityName?.trim();
	const [confirmFollow, setConfirmFollow] = useState(false);
	const ownList = activityHasOwnSegments({ segments });

	return (
		<Box data-testid={`segment-editor-activity-${activityIndex}`}>
			<Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
				Program Segments
			</Typography>

			{ownList ? (
				<Box
					data-testid="segments-own"
					sx={{
						border: 2,
						borderColor: "primary.dark",
						borderRadius: 1,
						overflow: "hidden",
						bgcolor: "background.paper",
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "flex-start",
							gap: 1.5,
							px: 2,
							py: 1.5,
							bgcolor: "primary.dark",
							color: "primary.contrastText",
						}}
					>
						<LinkOffIcon fontSize="small" sx={{ mt: 0.25 }} />
						<Box>
							<Typography
								variant="subtitle2"
								fontWeight="bold"
								sx={{ textTransform: "uppercase" }}
							>
								Own segments — not following template
							</Typography>
							<Typography variant="body2">
								{name
									? `Changes to ${name}'s segments do not apply to this activity.`
									: "Changes to the template's segments do not apply to this activity."}
							</Typography>
						</Box>
					</Box>
					<Box sx={{ p: 2 }}>
						<SegmentEditor
							segments={segments ?? []}
							onChange={onChange}
							inertThresholdNotices={inertThresholdNotices}
							rewardEngineOptions={rewardEngineOptions}
							behaviors={behaviors}
							cmVisitScheduleOptions={cmVisitScheduleOptions}
							templateDefaults={activityDefaults}
							hideHeading
							// Always an activity's editor here, so never undefined (which means the template's
							// editor): a missing name still reads "Inherits from this activity" (Q-6).
							activityName={activityName ?? ""}
						/>
					</Box>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap",
							gap: 2,
							px: 2,
							py: 1.5,
							borderTop: 1,
							borderColor: "divider",
						}}
					>
						<Button
							variant="outlined"
							size="small"
							startIcon={<LinkIcon />}
							data-testid="segments-follow-template"
							onClick={() => setConfirmFollow(true)}
						>
							Follow template segments
						</Button>
						<Typography variant="body2" color="text.secondary">
							{followTemplateHelperText(segments?.length ?? 0)}
						</Typography>
					</Box>
				</Box>
			) : (
				<Box
					data-testid="segments-following-template"
					sx={{
						border: "2px dashed",
						borderColor: (theme) =>
							(theme.palette as { brandFaint?: { main?: string } }).brandFaint
								?.main ?? theme.palette.primary.light,
						borderRadius: 1,
						p: 2,
						bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
					}}
				>
					<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
						<LinkIcon color="primary" sx={{ mt: 0.25 }} />
						<Box>
							<Typography
								variant="subtitle2"
								fontWeight="bold"
								color="primary"
								sx={{ textTransform: "uppercase" }}
							>
								{name ? `Following template — ${name}` : "Following template"}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								This activity uses the template&apos;s segments. Changes to the
								template&apos;s segments apply here too.
							</Typography>
						</Box>
					</Box>

					<Box sx={{ mt: 1.5 }}>
						{hasSegments(templateSegments) ? (
							templateSegments.map((segment, index) => (
								<Box
									key={index}
									data-testid={`segments-inherited-row-${index + 1}`}
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										py: 1.25,
										px: 0.5,
										borderTop: 1,
										borderColor: "divider",
									}}
								>
									<Typography variant="body2" color="text.secondary">
										{segmentHeaderSummary(
											segment,
											index,
											rewardEngineOptions,
											templateRewardEngineId,
										)}
									</Typography>
									<LockOutlinedIcon
										fontSize="small"
										color="action"
										aria-label="Read-only: follows the template"
									/>
								</Box>
							))
						) : (
							<Typography variant="body2" color="text.secondary">
								{NO_SEGMENTS_MESSAGE}
							</Typography>
						)}
					</Box>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap",
							gap: 2,
							mt: 1.5,
						}}
					>
						<Button
							variant="outlined"
							size="small"
							startIcon={<EditOutlinedIcon />}
							data-testid="segments-define-own"
							onClick={() => onChange(seedActivitySegments(templateSegments))}
						>
							Define own segments
						</Button>
						<Typography variant="body2" color="text.secondary">
							Starts from a copy of the template&apos;s segments.
						</Typography>
					</Box>
				</Box>
			)}

			{confirmFollow && (
				<BasicDialog
					title="Follow template segments?"
					onCancel={() => setConfirmFollow(false)}
					actions={
						<>
							<Button
								size="large"
								variant="outlined"
								onClick={() => setConfirmFollow(false)}
							>
								Cancel
							</Button>
							<Button
								size="large"
								variant="contained"
								data-testid="segments-follow-template-confirm"
								onClick={() => {
									setConfirmFollow(false);
									onFollowTemplate();
								}}
							>
								Follow template
							</Button>
						</>
					}
				>
					<Typography variant="body2">
						{`${activityLabel ? `${activityLabel}'s` : "This activity's"} own segments will be removed, and it will use ${name ? `${name}'s` : "the template's"} segments again. Nothing is saved until you update the treatment plan.`}
					</Typography>
				</BasicDialog>
			)}
		</Box>
	);
};

export default ActivitySegments;
